import type { AccountNode, SiteNode, VlanNode, VlanTemplateOut } from "../../api/types/allocation";

export interface OrgAccount {
  slug: string;
  display_name: string;
}
export interface OrgSite {
  accountSlug: string;
  siteSlug: string;
  displayName: string;
}

export type AllocNodeKind =
  | "root" | "account" | "site" | "vlan" | "legacyGroup" | "legacyAccount" | "legacySite";

export interface AllocNode {
  key: string;                 // unique; used as ?node= value
  kind: AllocNodeKind;
  label: string;
  cidr: string | null;
  allocated: boolean;
  source: string | null;       // 'allocated' | 'legacy' | null
  refSlug?: string;            // account/site slug for write targeting
  vlanTag?: number | null;
  dhcpRanges?: VlanNode["dhcp_ranges"];
  detail?: Record<string, unknown>;
  children: AllocNode[];
}

interface Input {
  accounts: OrgAccount[];
  orgSites: OrgSite[];
  ledger: AccountNode[];
  vlanTemplate: VlanTemplateOut[];
}

export function buildAllocationTree(input: Input): AllocNode {
  const tplByTag = new Map(input.vlanTemplate.map((t) => [t.vlan_tag, t]));
  const ledgerByAcct = new Map(
    input.ledger.filter((a) => a.account_slug).map((a) => [a.account_slug as string, a]),
  );

  // org accounts -> nodes (allocated where a ledger row matches)
  const accountNodes: AllocNode[] = input.accounts.map((acct) => {
    const led = ledgerByAcct.get(acct.slug) ?? null;
    const siteLedger = new Map(
      (led?.sites ?? []).filter((s) => s.site_slug).map((s) => [s.site_slug as string, s]),
    );
    const orgSitesForAcct = input.orgSites.filter((s) => s.accountSlug === acct.slug);
    const orgSlugs = new Set(orgSitesForAcct.map((s) => s.siteSlug));
    const sites = orgSitesForAcct.map((s) =>
      siteNode(acct.slug, s, siteLedger.get(s.siteSlug) ?? null, tplByTag));
    // Ledger child sites that match no org site (null site_slug, or a slug not in
    // org data) still reserve CIDR space — surface them as legacy/unmatched nodes
    // (with their VLANs) so the estate is complete, not silently dropped.
    const unmatchedSites = (led?.sites ?? [])
      .filter((s) => !s.site_slug || !orgSlugs.has(s.site_slug))
      .map((s) => legacySiteNode(`account:${acct.slug}`, s, tplByTag));
    return {
      key: `account:${acct.slug}`,
      kind: "account",
      label: `${acct.display_name} (${acct.slug})`,
      cidr: led?.cidr ?? null,
      allocated: Boolean(led?.cidr),
      source: led?.source ?? null,
      refSlug: acct.slug,
      children: [...sites, ...unmatchedSites],
    };
  });

  // ledger rows that match no org account (legacy / unmatched reservations)
  const matchedAcct = new Set(input.accounts.map((a) => a.slug));
  const legacyNodes: AllocNode[] = input.ledger
    .filter((a) => !a.account_slug || !matchedAcct.has(a.account_slug))
    .map((a) => legacyAccountNode(a, tplByTag));

  const children: AllocNode[] = [...accountNodes];
  if (legacyNodes.length > 0) {
    children.push({
      key: "legacy",
      kind: "legacyGroup",
      label: `Legacy / unmatched reservations (${legacyNodes.length})`,
      cidr: null,
      allocated: true,
      source: "legacy",
      children: legacyNodes,
    });
  }

  return {
    key: "root",
    kind: "root",
    label: "Orixen supernet",
    cidr: "10.128.0.0/9",
    allocated: true,
    source: null,
    children,
  };
}

function siteNode(
  accountSlug: string,
  org: OrgSite,
  led: SiteNode | null,
  tplByTag: Map<number, VlanTemplateOut>,
): AllocNode {
  return {
    key: `site:${accountSlug}:${org.siteSlug}`,
    kind: "site",
    label: `${org.displayName} (${org.siteSlug})`,
    cidr: led?.cidr ?? null,
    allocated: Boolean(led?.cidr),
    source: led?.source ?? null,
    refSlug: org.siteSlug,
    children: (led?.vlans ?? []).map((v) => vlanNode(`site:${accountSlug}:${org.siteSlug}`, v, tplByTag)),
  };
}

function vlanNode(parentKey: string, v: VlanNode, tplByTag: Map<number, VlanTemplateOut>): AllocNode {
  const tpl = v.vlan_tag != null ? tplByTag.get(v.vlan_tag) : undefined;
  const name = tpl?.name ?? (v.vlan_tag != null ? `vlan${v.vlan_tag}` : "vlan");
  return {
    key: `${parentKey}:vlan:${v.vlan_tag ?? v.id}`,
    kind: "vlan",
    label: `${v.vlan_tag ?? "?"} · ${name}`,
    cidr: v.cidr,
    allocated: true,
    source: v.source,
    vlanTag: v.vlan_tag,
    dhcpRanges: v.dhcp_ranges,
    detail: tpl ? { prefix_len: tpl.prefix_len, offset_units: tpl.offset_units } : {},
    children: [],
  };
}

function legacySiteNode(parentKey: string, s: SiteNode, tplByTag: Map<number, VlanTemplateOut>): AllocNode {
  const id = s.inventory_slug ?? s.site_slug ?? s.id;
  const key = `${parentKey}:legacy-site:${id}`;
  return {
    key,
    kind: "legacySite",
    label: `${id}${s.site_slug ? "" : " (unmatched)"}`,
    cidr: s.cidr,
    allocated: true,
    source: s.source,   // real provenance (legacy OR allocated) — the GROUP is the "unmatched" bucket
    children: s.vlans.map((v) => vlanNode(key, v, tplByTag)),   // include VLAN children
  };
}

function legacyAccountNode(a: AccountNode, tplByTag: Map<number, VlanTemplateOut>): AllocNode {
  const id = a.inventory_slug ?? a.cidr ?? a.id;
  const key = `legacy:${id}`;
  return {
    key,
    kind: "legacyAccount",
    label: `${id}${a.account_slug ? "" : " (unmatched)"}`,
    cidr: a.cidr,
    allocated: true,
    source: a.source,   // real provenance (legacy OR allocated-with-missing-FK), not hard-coded
    children: a.sites.map((s) => legacySiteNode(key, s, tplByTag)),
  };
}
